import { Tab, Tabs } from "@mui/material";
import classNames from "classnames";
import React, { FC, Fragment } from "react";

export type TabVariant = "default" | "underline" | "pill";

interface TabHeaderProps {
  tabs: {
    value: number;
    text: string;
    Icon?: any;
    badge?: React.ReactNode;
  }[];
  selectedTab: number;
  setSelectedTab: (selectedTab: number) => void;
  containerClassName?: string;
  variant?: TabVariant;
}

const baseTabSx = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  minWidth: "auto",
  textTransform: "none",
  fontFamily: "inherit",
  "& .MuiTab-iconWrapper": { mb: 0 },
} as const;

// Icon colors stay literal hex: they flow into SVG presentation ATTRIBUTES,
// where var() does not resolve. The gold matches --accent in both themes.
const renderIcon = (Icon: any, isActive: boolean, inactiveStroke: string) =>
  Icon ? (
    <Icon
      fill={isActive ? "#AB854A" : "#fff"}
      stroke={isActive ? "#AB854A" : inactiveStroke}
    />
  ) : (
    <Fragment key="tab" />
  );

export const TabHeader: FC<TabHeaderProps> = ({
  tabs,
  selectedTab,
  setSelectedTab,
  containerClassName = "",
  variant = "default",
}) => {
  const onChange = (_: unknown, e: number) => setSelectedTab(e);

  if (variant === "underline") {
    return (
      <Tabs
        value={selectedTab}
        onChange={onChange}
        aria-label="tab header"
        className={containerClassName}
        TabIndicatorProps={{
          sx: {
            backgroundColor: "var(--accent)",
            height: "3px",
            borderRadius: "2px 2px 0 0",
          },
        }}
        sx={{ "& .MuiTabs-flexContainer": { gap: 1 } }}
      >
        {tabs.map(({ value, text, Icon, badge }) => {
          const isActive = selectedTab === value;
          return (
            <Tab
              key={value}
              value={value}
              disableRipple
              icon={renderIcon(Icon, isActive, "#B0B0B0")}
              label={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {text}
              {badge}
            </span>
          }
              sx={{
                ...baseTabSx,
                gap: 1,
                minHeight: 36,
                fontSize: 14,
                fontWeight: 500,
                letterSpacing: 0.1,
                color: isActive ? "var(--accent-text)" : "var(--text-2)",
                px: 1.75,
                pt: 1.25,
                pb: 1.25,
                borderRadius: 0,
                transition: "color 0.15s",
                "&.Mui-selected": { color: "var(--accent-text)", fontWeight: 600 },
                "&:hover": {
                  color: isActive ? "var(--accent-text)" : "var(--text-2)",
                  bgcolor: "transparent",
                },
              }}
            />
          );
        })}
      </Tabs>
    );
  }

  if (variant === "pill") {
    return (
      <Tabs
        value={selectedTab}
        onChange={onChange}
        aria-label="tab header"
        className={containerClassName}
        TabIndicatorProps={{ sx: { display: "none" } }}
        sx={{
          minHeight: 0,
          alignSelf: "flex-start",
          display: "inline-flex",
          width: "fit-content",
          bgcolor: "var(--surface-2)",
          borderRadius: "10px",
          padding: "4px",
          "& .MuiTabs-flexContainer": { gap: "4px" },
          "& .MuiTabs-scroller": { overflow: "visible !important" },
        }}
      >
        {tabs.map(({ value, text, Icon, badge }) => {
          const isActive = selectedTab === value;
          return (
            <Tab
              key={value}
              value={value}
              disableRipple
              icon={renderIcon(Icon, isActive, "#9B958C")}
              label={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {text}
              {badge}
            </span>
          }
              sx={{
                ...baseTabSx,
                justifyContent: "center",
                gap: 0.75,
                minHeight: 36,
                fontSize: 13,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? "var(--text)" : "var(--text-2)",
                px: 1.75,
                py: 0.75,
                borderRadius: "8px",
                bgcolor: isActive ? "var(--surface)" : "transparent",
                boxShadow: isActive ? "var(--sh-1)" : "none",
                transition: "background-color 0.15s, color 0.15s",
                "&.Mui-selected": { color: "var(--text)", fontWeight: 600 },
                "&:hover": {
                  color: "var(--text)",
                  bgcolor: "var(--surface)",
                },
              }}
            />
          );
        })}
      </Tabs>
    );
  }

  // Default: original cream-pill highlight on active. Used app-wide.
  return (
    <Tabs
      value={selectedTab}
      onChange={onChange}
      aria-label="tab header"
      className={containerClassName}
      TabIndicatorProps={{ sx: { display: "none" } }}
    >
      {tabs.map(({ value, text, Icon, badge }) => (
        <Tab
          key={value}
          value={value}
          className={classNames(
            "!flex !flex-row gap-1.5 !border-0 !min-h-40 !rounded-md",
            { "!bg-tint": selectedTab === value }
          )}
          icon={renderIcon(Icon, selectedTab === value, "#B0B0B0")}
          label={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {text}
              {badge}
            </span>
          }
        />
      ))}
    </Tabs>
  );
};
